/**
 * The one figure a listing ADVERTISES, in AUD.
 *
 * WHY THIS EXISTS. Three modules derived this independently and only two of them
 * agreed:
 *
 *   - `product-card.tsx` resolved a model-aware `displayPrice`, because without
 *     it "the card would read A$0.00 for every tiered product";
 *   - `public-catalog.ts` resolved a model-aware `CatalogPrice` for the
 *     server-rendered browse and tag pages;
 *   - `product-filters.ts` resolved `discountedPrice || price || 0` for EVERY
 *     model, and that is the one the marketplace, the storefront and /search
 *     filtered and sorted on.
 *
 * Tiered and pay-what-you-want listings carry no meaningful top-level `price`
 * (both product forms write `toUsd(Number(standardPrice) || 0)`, and that field
 * is empty for a seller who picked Tiers or PWYW), so the third answer was 0.
 * A shopper who set any minimum price lost every tiered and PWYW listing on the
 * site, and "Price: Low to High" put them all first as though they were free,
 * while the card beside them advertised "From A$25".
 *
 * The fix is not a fourth copy. This is the resolver; the three call sites
 * above delegate to it, so a new pricing model is taught to the site once.
 *
 * PURE, like price-bounds.ts and free-pricing.ts beside it, and importing only
 * price-bounds.ts (which has no imports), so it runs under the
 * `--experimental-strip-types` test runner and can be imported by a
 * server-only module, a client component and a lib alike.
 *
 * THIS IS A DISPLAY FIGURE, NEVER A CHARGE. What a buyer actually pays is
 * resolved server-side by `resolveItemPriceAud` in functions/src/index.ts, from
 * the tier or amount they chose at the product page. Nothing here should ever
 * be summed into a total.
 */

import { PRICE_MIN_AUD } from './price-bounds.ts';
import { wholeCentFloorAud } from './pwyw-seed.ts';

/** The shape any product document offers up. Deliberately loose: the three
 *  callers pass a Firestore document, a `Product`, and a card's props. */
export type PricedListing = {
  pricingModel?: string | null;
  price?: number | null;
  discountedPrice?: number | null;
  standardPrice?: number | null;
  priceTiers?: ReadonlyArray<{ price?: unknown } | null | undefined> | null;
  pwywMinPrice?: number | null;
  pwywMinPriceEnabled?: boolean | null;
  subscriptionPrice?: number | null;
};

/**
 * The tier a tiered listing advertises: the cheapest one.
 *
 * Returned as the TIER rather than its price because the card also needs the
 * tier's `subscriptionFrequency` to render "A$10 monthly", and picking the
 * cheapest tier twice (once for the number, once for the cadence) is how the
 * number and the cadence come to describe different tiers.
 *
 * `null` for a listing with no tiers at all. That is a real state: the create
 * form starts a product with tiers, but a document can reach the browse
 * surfaces with an empty array, and `Array.prototype.reduce` on an empty array
 * throws rather than returning undefined.
 */
export function cheapestPriceTier<T extends { price?: unknown }>(
  priceTiers: ReadonlyArray<T | null | undefined> | null | undefined,
): T | null {
  if (!Array.isArray(priceTiers) || priceTiers.length === 0) return null;
  let lowest: T | null = null;
  for (const tier of priceTiers) {
    if (!tier) continue;
    if (lowest === null || (Number(tier.price) || 0) < (Number(lowest.price) || 0)) {
      lowest = tier;
    }
  }
  return lowest;
}

/**
 * Whether the advertised figure is a FLOOR rather than the price.
 *
 * Tiered and PWYW listings advertise "from"; everything else advertises the
 * price itself. Exported so a surface that renders the number can label it
 * honestly, which is the difference between "A$25" and "From A$25".
 */
export function listingPriceIsFrom(product: PricedListing): boolean {
  return product.pricingModel === 'tiered' || product.pricingModel === 'pwyw';
}

/**
 * The advertised price, in AUD.
 *
 * One branch per pricing model, in the order the models were added:
 *
 *   free          0, and no price field is consulted to decide that. Same rule
 *                 as `cartPriceAud` in free-pricing.ts, and for the same
 *                 reason: a listing switched to Free after a price was typed
 *                 keeps that amount parked on the document, and the server
 *                 charges 0 regardless, so anything else advertises a price
 *                 nobody will be charged.
 *   tiered        the cheapest tier. The top-level `price` is meaningless here.
 *   pwyw          the seller's floor when they set one, otherwise 0. A PWYW
 *                 listing with no floor genuinely starts at nothing.
 *   subscription  the per-cycle price. `price` is the fallback for documents
 *                 written before `subscriptionPrice` split out of it.
 *   standard      the sale price when one is running, otherwise the list price.
 *
 * The standard branch keeps the truthy-or that every existing reader used
 * (`discountedPrice || price`) rather than a `typeof === 'number'` test. The two
 * differ only on a `discountedPrice` of exactly 0, which cannot legitimately
 * exist: PRICE_MIN_AUD is 1 and a zero-priced listing is the `free` model,
 * which returned above. `null` is what free-pricing.ts writes to clear a
 * discount, and `Number(null) || price` falls through to the list price
 * correctly.
 */
export function listingPriceAud(product: PricedListing): number {
  if (!product) return 0;

  if (product.pricingModel === 'free') return 0;

  if (product.pricingModel === 'tiered') {
    const tier = cheapestPriceTier(product.priceTiers);
    return tier ? Math.max(0, Number(tier.price) || 0) : 0;
  }

  if (product.pricingModel === 'pwyw') {
    const hasFloor = product.pwywMinPriceEnabled === true && typeof product.pwywMinPrice === 'number';
    return hasFloor ? Math.max(0, product.pwywMinPrice as number) : 0;
  }

  if (product.pricingModel === 'subscription') {
    return Math.max(0, Number(product.subscriptionPrice ?? product.price) || 0);
  }

  return Math.max(0, Number(product.discountedPrice) || Number(product.price) || 0);
}

/**
 * What a card prints where the price goes, by pricing model.
 *
 *   free   "Free". No figure, and no VAT qualifier: nothing is charged.
 *   pwyw   "Buyer's choice", plus the seller's floor ("A$5+") when they set
 *          one. The qualifier stays even without a floor, because whatever the
 *          buyer names is charged VAT for an EU or UK buyer (and checkout
 *          never takes less than PRICE_MIN_AUD).
 *   price  The advertised figure from listingPriceAud, with "From" and the
 *          cadence exactly as before.
 *
 * Display only, like everything here: sorting and the price filters keep
 * reading listingPriceAud, so a PWYW listing still sorts by its floor and a
 * free one at 0.
 */
export type ListingPriceDisplay =
  | { kind: 'free' }
  | { kind: 'pwyw'; minimumAud: number | null }
  | { kind: 'price'; amountAud: number };

export function listingPriceDisplay(product: PricedListing): ListingPriceDisplay {
  if (product?.pricingModel === 'free') return { kind: 'free' };
  if (product?.pricingModel === 'pwyw') {
    const floor = listingPriceAud(product);
    // A floor under the platform minimum (a legacy document) is shown as the
    // minimum checkout actually takes, as the product page does.
    return { kind: 'pwyw', minimumAud: floor > 0 ? Math.max(PRICE_MIN_AUD, floor) : null };
  }
  return { kind: 'price', amountAud: listingPriceAud(product) };
}

/** The price a server-rendered catalog page holds for a listing (public-catalog CatalogPrice). */
export type CatalogPriceText = {
  amount: number;
  from: boolean;
  free: boolean;
  recurring: string | null;
  /** Absent on a catalog read cached before the flag existed. */
  pwyw?: boolean;
};

/**
 * The one price string /browse and the tag pages print for a listing, worded as
 * the product card words it (listingPriceDisplay): "Free" rather than A$0.00, and
 * "Buyer's choice" with the floor as "A$5.00+" rather than "From A$5.00", which
 * reads as a tiered price. These pages print AUD, as the catalog is priced.
 *
 * @param {CatalogPriceText} price The listing's catalog price.
 * @return {string} The label.
 */
export function catalogPriceLabel(price: CatalogPriceText): string {
  const aud = (n: number) => `A$${n.toFixed(2)}`;
  if (price.free) return 'Free';
  // A floorless pay-what-you-want listing is the only one with a zero "from".
  // The floor is stored as a raw conversion (A$7.5414); checkout takes the
  // whole cent at or above it, so that cent is what is stated, as on the
  // product page.
  if (price.pwyw || (price.from && price.amount <= 0)) {
    return price.amount > 0 ?
      `Buyer's choice, ${aud(wholeCentFloorAud(Math.max(PRICE_MIN_AUD, price.amount)))}+` :
      "Buyer's choice";
  }
  if (price.from) return `From ${aud(price.amount)}`;
  if (price.recurring) return `${aud(price.amount)} ${price.recurring}`;
  return aud(price.amount);
}
